import styled from "styled-components";

const bp = { md: "768px" };

const colors = {
  bgTop: "#fff7f8",
  bgBottom: "#f7efe7",

  white: "#ffffff",
  text: "#1f1612",
  textSoft: "#5f514a",
  textMuted: "#7f7068",

  brown: "#2b1d17",
  brownDark: "#170f0c",

  gold: "#c9a227",
  goldDark: "#94703c",
  goldLight: "#f6ead1",

  pink: "#d792a9",
  pinkDark: "#aa627c",
  pinkSoft: "#fff1f6",
  pinkSoft2: "#fcf4f7",

  border: "#dcc9ae",
  borderSoft: "#e8d8c3",
  borderPink: "#ddb7c4",
  borderGold: "#d5b47a",

  successText: "#0f5c35",
  successBg: "#e7f5ec",
  successBorder: "#bddcc9",

  dangerText: "#91445f",
  dangerBg: "#fff0f5",
  dangerBorder: "#e8c2cf",
};

export const PageWrap = styled.div`
  min-height: 100vh;
  padding: 95px 20px 40px;
  background:
    radial-gradient(
      circle at top right,
      rgba(217, 163, 180, 0.12),
      transparent 22%
    ),
    radial-gradient(
      circle at top left,
      rgba(199, 165, 106, 0.08),
      transparent 24%
    ),
    linear-gradient(180deg, #fffaf7 0%, ${colors.bgTop} 100%);

  @media (max-width: 768px) {
    padding: 70px 14px 28px;
  }
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 26px;
  margin-bottom: 28px;
`;

export const HeaderBottom = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 390px minmax(0, 1fr);
  gap: 22px;
  align-items: center;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

export const HeaderLeft = styled.div`
  width: 100%;
  display: flex;
`;

export const HeaderCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export const HeaderRight = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (max-width: 1080px) {
    justify-content: center;
  }
`;

export const Title = styled.h1`
  margin: 6px 0 10px;
  font-family: "Cormorant Garamond", serif;
  font-weight: 650;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: 30px;
  color: rgba(0, 0, 0, 0.92);
  text-align: center;

  @media (max-width: ${bp.md}) {
    font-size: 26px;
    letter-spacing: 0.12em;
  }
`;

export const Subtitle = styled.p`
  margin: 0;
  color: ${colors.textSoft};
  font-size: 15px;
  line-height: 1.55;
  max-width: 640px;
  text-align: center;
`;

export const Ornament = styled.div`
  position: relative;
  width: 260px;
  height: 14px;
  margin: 6px auto 14px;
`;

export const OrnLine = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  transform: translateY(-50%);
  background: rgba(201, 162, 39, 0.42);
  border-radius: 999px;
`;

export const OrnDiamond = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 10px;
  height: 10px;
  transform: translate(-50%, -50%) rotate(45deg);
  background: #c9a227;
  border-radius: 2px;
  box-shadow:
    0 0 0 3px rgba(201, 162, 39, 0.14),
    0 1px 2px rgba(0, 0, 0, 0.12);
`;

export const Badge = styled.div`
  padding: 8px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #fff9ec 0%, #fff3f7 100%);
  border: 1px solid ${colors.borderGold};
  color: ${colors.goldDark};
  font-weight: 800;
  letter-spacing: 0.02em;
  box-shadow: 0 8px 18px rgba(199, 165, 106, 0.08);
  white-space: nowrap;
`;

export const BaseButton = styled.button`
  border: none;
  border-radius: 14px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease;
  min-height: 46px;
  letter-spacing: 0.01em;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const PrimaryButton = styled(BaseButton)`
  background: linear-gradient(135deg, ${colors.brown} 0%, #5f3f3a 100%);
  color: #fff;
  box-shadow: 0 12px 24px rgba(47, 34, 26, 0.16);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${colors.brownDark} 0%, #734d5a 100%);
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(47, 34, 26, 0.2);
  }
`;

export const SecondaryButton = styled(BaseButton)`
  background: linear-gradient(135deg, #fffaf3 0%, #fff4f8 100%);
  color: #6d4757;
  border: 1px solid ${colors.borderPink};
  box-shadow: 0 6px 16px rgba(217, 163, 180, 0.08);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #fff5ea 0%, #ffeef5 100%);
    border-color: #d7b28e;
    transform: translateY(-1px);
  }
`;

export const DangerButton = styled(BaseButton)`
  background: linear-gradient(135deg, #a1283d 0%, #7e1f31 100%);
  color: #fff;
  box-shadow: 0 10px 22px rgba(161, 40, 61, 0.18);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #8e2135 0%, #671726 100%);
    transform: translateY(-1px);
  }
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: 390px minmax(0, 1fr);
  gap: 22px;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

export const Sidebar = styled.aside`
  min-width: 0;
`;

export const Main = styled.main`
  min-width: 0;
`;

export const Panel = styled.div`
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(255, 251, 247, 0.98) 100%
  );
  border: 1.5px solid rgba(213, 180, 122, 0.62);
  border-radius: 24px;
  padding: 20px;
  box-shadow:
    0 20px 50px rgba(45, 27, 18, 0.1),
    0 10px 24px rgba(201, 162, 39, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);

  @media (max-width: 768px) {
    border-radius: 20px;
    padding: 16px;
  }
`;

export const PanelTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 21px;
  color: ${colors.text};
  letter-spacing: -0.01em;
`;

export const SearchInput = styled.input`
  width: 100%;
  height: 48px;
  border-radius: 14px;
  border: 1px solid ${colors.border};
  padding: 0 14px;
  font-size: 14px;
  outline: none;
  margin-bottom: 12px;
  background: linear-gradient(180deg, #fff 0%, #fffdfb 100%);
  color: ${colors.text};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;

  &::placeholder {
    color: #9d918a;
  }

  &:focus {
    border-color: ${colors.pinkDark};
    box-shadow: 0 0 0 4px rgba(185, 119, 141, 0.12);
    background: #fff;
  }
`;

export const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

export const Select = styled.select`
  width: 100%;
  height: 48px;
  border-radius: 14px;
  border: 1.5px solid ${({ $error }) => ($error ? "#c14b68" : colors.border)};
  padding: 0 14px;
  font-size: 14px;
  background: linear-gradient(180deg, #fff 0%, #fffdfb 100%);
  outline: none;
  color: ${colors.text};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;

  &:focus {
    border-color: ${colors.pinkDark};
    box-shadow: 0 0 0 4px rgba(185, 119, 141, 0.12);
    background: #fff;
  }
`;

export const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 70vh;
  overflow: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(
      180deg,
      rgba(199, 165, 106, 0.7),
      rgba(217, 163, 180, 0.7)
    );
    border-radius: 999px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(248, 243, 237, 0.8);
    border-radius: 999px;
  }
`;

export const ProductCard = styled.div`
  border: 1.5px solid
    ${({ $active }) => ($active ? colors.gold : colors.border)};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, #fff8ee 0%, #fff0f5 100%)"
      : "linear-gradient(180deg, #ffffff 0%, #fffaf5 100%)"};
  border-radius: 18px;
  padding: 15px;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;
  box-shadow: ${({ $active }) =>
    $active
      ? "0 16px 32px rgba(201, 162, 39, 0.14)"
      : "0 8px 18px rgba(45, 27, 18, 0.07)"};

  &:hover {
    border-color: ${colors.gold};
    transform: translateY(-2px);
    box-shadow:
      0 18px 34px rgba(201, 162, 39, 0.14),
      0 10px 22px rgba(217, 163, 180, 0.1);
  }
`;

export const ProductTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
`;

export const ProductName = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  color: ${colors.text};
  line-height: 1.3;
`;

export const ProductMeta = styled.div`
  color: ${colors.textSoft};
  font-size: 13px;
  line-height: 1.45;
`;

export const ProductStatus = styled.div`
  white-space: nowrap;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  color: ${({ $ok }) => ($ok ? colors.successText : colors.dangerText)};
  background: ${({ $ok }) => ($ok ? colors.successBg : colors.dangerBg)};
  border: 1px solid
    ${({ $ok }) => ($ok ? colors.successBorder : colors.dangerBorder)};
`;

export const ProductPrice = styled.div`
  margin-top: 10px;
  font-size: 14px;
  font-weight: 800;
  color: ${colors.brown};
`;

export const ProductActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;

  button {
    padding: 9px 12px;
    min-height: auto;
    font-size: 13px;
    border-radius: 12px;
  }
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: ${colors.brown};
  font-weight: 900;
  letter-spacing: 0.015em;
  text-transform: uppercase;
`;

export const SectionHint = styled.p`
  margin: -4px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${colors.textSoft};
  max-width: 760px;
`;

export const StatusBox = styled.div`
  padding: 13px 15px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $ok }) => ($ok ? colors.successText : colors.dangerText)};
  background: ${({ $ok }) =>
    $ok
      ? "linear-gradient(135deg, #eef9f2 0%, #f7fdf9 100%)"
      : "linear-gradient(135deg, #fff4f7 0%, #fff9fb 100%)"};
  border: 1px solid
    ${({ $ok }) => ($ok ? colors.successBorder : colors.dangerBorder)};
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ToggleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const FieldError = styled.div`
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.4;
  color: #c14b68;
  font-weight: 600;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #473831;
  font-weight: 800;
  font-size: 14px;
  line-height: 1.2;
`;

export const Input = styled.input`
  width: 100%;
  height: 48px;
  border-radius: 14px;
  border: 1.5px solid ${({ $error }) => ($error ? "#c14b68" : colors.border)};
  padding: 0 14px;
  font-size: 14px;
  outline: none;
  background: linear-gradient(180deg, #fff 0%, #fffdfa 100%);
  color: ${colors.text};
  box-shadow: inset 0 1px 2px rgba(41, 24, 18, 0.03);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    opacity 0.2s ease;

  &::placeholder {
    color: #9a877d;
  }

  &:focus {
    border-color: ${colors.gold};
    box-shadow:
      0 0 0 4px rgba(201, 162, 39, 0.14),
      0 6px 14px rgba(201, 162, 39, 0.08);
    background: #fff;
  }

  &:disabled {
    background: linear-gradient(180deg, #f3ece4 0%, #ebe1d5 100%);
    color: #8a7a71;
    border-color: #d6c5b0;
    cursor: not-allowed;
    opacity: 1;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 130px;
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 14px;
  outline: none;
  resize: vertical;
  background: linear-gradient(180deg, #fff 0%, #fffdfb 100%);
  color: ${colors.text};
  line-height: 1.55;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;

  &::placeholder {
    color: #9d918a;
  }

  border: 1.5px solid ${colors.border};
  box-shadow: inset 0 1px 2px rgba(41, 24, 18, 0.03);

  &:focus {
    border-color: ${colors.gold};
    box-shadow:
      0 0 0 4px rgba(201, 162, 39, 0.14),
      0 6px 14px rgba(201, 162, 39, 0.08);
  }
`;

export const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  cursor: pointer;
  padding: 0 14px;
  border: 1px solid ${colors.borderSoft};
  border-radius: 16px;
  background: linear-gradient(135deg, #fffdf8 0%, #fff8fb 100%);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    border-color: ${colors.borderGold};
    box-shadow: 0 10px 20px rgba(199, 165, 106, 0.06);
  }

  input {
    width: 18px;
    height: 18px;
    accent-color: ${colors.pinkDark};
    flex: 0 0 auto;
  }
`;

export const CheckboxLabel = styled.span`
  color: #3f352f;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
`;

export const HelperText = styled.div`
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.55;
  color: ${colors.textMuted};
`;

export const InlineRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ImagePreview = styled.img`
  display: block;
  width: 100%;
  max-width: 300px;
  height: 190px;
  object-fit: cover;
  border-radius: 18px;
  margin-top: 12px;
  border: 1px solid ${colors.borderGold};
  box-shadow:
    0 16px 30px rgba(199, 165, 106, 0.08),
    0 8px 20px rgba(217, 163, 180, 0.08);
`;

export const EmptyState = styled.div`
  padding: 18px;
  border: 1px dashed ${colors.borderGold};
  border-radius: 16px;
  color: ${colors.textSoft};
  background: linear-gradient(135deg, #fffcf6 0%, #fff7fb 100%);
`;

export const InfoBox = styled.div`
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #fffdf9 0%, #fff8fb 100%);
  border: 1px solid ${colors.border};
  color: #5e534d;
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(201, 162, 39, 0.28);
  margin: 22px 0;
`;

export const SmallText = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: ${colors.textMuted};
`;

export const ForbiddenWrap = styled.div`
  min-height: 70vh;
  display: flex;
  padding: 24px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  text-align: center;

  h2 {
    margin: 0;
    color: ${colors.text};
  }

  p {
    margin: 0;
    color: ${colors.textSoft};
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-width: 160px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid ${colors.borderGold};
  background: linear-gradient(135deg, #fff9ed 0%, #fff3f8 100%);
  color: #7a5561;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #fff4e3 0%, #ffeef5 100%);
    border-color: ${colors.gold};
    transform: translateY(-1px);
    box-shadow:
      0 12px 24px rgba(199, 165, 106, 0.08),
      0 8px 20px rgba(217, 163, 180, 0.08);
  }
`;

export const MessageBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  margin-bottom: 18px;
  border-radius: 18px;
  border: 1px solid
    ${({ $type }) => ($type === "error" ? "#efc9d4" : "#d7e8d9")};
  background: ${({ $type }) =>
    $type === "error"
      ? "linear-gradient(135deg, #fff4f7 0%, #fff9fb 100%)"
      : "linear-gradient(135deg, #eef9f2 0%, #f9fdf9 100%)"};
  box-shadow: 0 12px 28px rgba(30, 20, 15, 0.05);
`;

export const MessageIcon = styled.div`
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 16px;
  color: #fff;
  background: ${({ $type }) =>
    $type === "error"
      ? "linear-gradient(135deg, #d07c98 0%, #b4617f 100%)"
      : "linear-gradient(135deg, #69a97d 0%, #4f8f63 100%)"};
`;

export const MessageContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const MessageTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #3d2d28;
  margin-bottom: 4px;
`;

export const MessageText = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: #6f625c;
`;

export const MessageClose = styled.button`
  border: none;
  background: transparent;
  color: #8d7c75;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  margin-top: -2px;

  &:hover {
    color: #4a3a34;
  }
`;

export const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
  margin-top: 14px;
`;

export const ImageCard = styled.div`
  padding: 12px;
  border-radius: 18px;
  border: 1px solid ${colors.borderGold};
  background: linear-gradient(180deg, #fffdf9 0%, #fff7fb 100%);
  box-shadow:
    0 10px 22px rgba(199, 165, 106, 0.06),
    0 8px 20px rgba(217, 163, 180, 0.06);
`;

export const ImageCardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;

  button {
    padding: 9px 12px;
    min-height: auto;
    font-size: 13px;
    border-radius: 12px;
  }
`;

export const ClosePageButton = styled.button`
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 99999;

  width: 46px;
  height: 46px;
  border-radius: 999px;
  border: 1px solid rgba(201, 162, 39, 0.38);

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(25, 22, 20, 0.92);
  color: #ff77b4;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 10px 24px rgba(0, 0, 0, 0.24);

  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(252, 154, 196, 0.7);
    background: rgba(38, 32, 30, 0.98);
  }
`;
